import { useEffect } from "react";

// Dependency-free head manager: sets title, description, canonical, Open Graph /
// Twitter tags, robots, and injects JSON-LD structured data per route. Googlebot
// renders JS, so this is picked up on crawl. Cleans up its JSON-LD on unmount.
export const SITE_URL = "https://askredline.com";
const DEFAULT_IMAGE = `${SITE_URL}/og-share.jpg`;

function upsertMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function Seo({
  title,
  description,
  canonical,
  image = DEFAULT_IMAGE,
  type = "website",
  noindex = false,
  jsonLd,
  scrollTop = false,
}) {
  useEffect(() => {
    // Same guard as lib/scroll: a crawler's partial DOM must never blank a marketing page.
    if (scrollTop && typeof window.scrollTo === "function") window.scrollTo(0, 0);
    if (title) {
      document.title = title;
      upsertMeta("property", "og:title", title);
      upsertMeta("name", "twitter:title", title);
    }
    if (description) {
      upsertMeta("name", "description", description);
      upsertMeta("property", "og:description", description);
      upsertMeta("name", "twitter:description", description);
    }
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", "Ask Redline");
    if (image) {
      upsertMeta("property", "og:image", image);
      upsertMeta("name", "twitter:image", image);
      upsertMeta("name", "twitter:card", "summary_large_image");
    }
    upsertMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow");

    const path = typeof window !== "undefined" ? window.location.pathname : "/";
    const href = canonical
      ? canonical.startsWith("http") ? canonical : `${SITE_URL}${canonical}`
      : `${SITE_URL}${path}`;
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", href);
    upsertMeta("property", "og:url", href);

    const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    const nodes = blocks.map((data) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", "1");
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
      return s;
    });
    return () => nodes.forEach((n) => n.remove());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, image, type, noindex, JSON.stringify(jsonLd)]);

  return null;
}

// Shared JSON-LD builders
export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export const faqLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: (faqs || []).map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});
